import consumer from "./consumer";

export class FormCollaborationChannel {
  constructor(formUuid, sessionId, callbacks = {}) {
    this.formUuid = formUuid;
    this.sessionId = sessionId;
    this.subscription = null;
    this.isConnected = false;
    this.callbacks = callbacks;
  }

  async connect() {
    if (this.subscription && this.isConnected) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      try {
        this.subscription = consumer.subscriptions.create(
          {
            channel: "FormCollaborationChannel",
            form_uuid: this.formUuid,
            session_id: this.sessionId,
          },
          {
            connected: () => {
              this.isConnected = true;
              this.triggerCallback("onConnectionStatusChanged", {
                status: "connected",
              });
              resolve();
            },

            disconnected: () => {
              this.isConnected = false;
              this.triggerCallback("onConnectionStatusChanged", {
                status: "disconnected",
              });
            },

            rejected: () => {
              console.error(
                `🚫 Rejected from FormCollaborationChannel for form ${this.formUuid}`,
              );
              this.isConnected = false;
              this.triggerCallback("onConnectionStatusChanged", {
                status: "rejected",
              });
              reject(new Error("Connection rejected"));
            },

            received: (data) => {
              this.handleMessage(data);
            },
          },
        );
      } catch (error) {
        console.error("Failed to create WebSocket connection:", error);
        this.triggerCallback("onConnectionStatusChanged", {
          status: "error",
          error: error.message,
        });
        reject(error);
      }
    });
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }

    this.isConnected = false;

    this.triggerCallback("onConnectionStatusChanged", {
      status: "disconnected",
    });
  }

  // WebSocket message handling
  handleMessage(data) {
    switch (data.type) {
      case "initial_sync":
        this.triggerCallback("onInitialSync", data);
        break;
      case "sync_response":
        this.triggerCallback("onSyncResponse", data);
        break;
      case "collaborator_joined":
        this.triggerCallback("onCollaboratorJoined", data);
        break;
      case "collaborator_left":
        this.triggerCallback("onCollaboratorLeft", data);
        break;
      case "field_focused":
        this.triggerCallback("onFieldFocused", data);
        break;
      case "field_updated":
        this.triggerCallback("onFieldUpdated", data);
        break;
      case "form_updated":
        this.triggerCallback("onFormUpdated", data);
        break;
      case "field_blurred":
        this.triggerCallback("onFieldBlurred", data);
        break;
      case "session_completed":
        this.triggerCallback("onSessionCompleted", data);
        break;
      default:
        console.warn("Unknown collaboration message type:", data.type);
    }
  }

  // Field interaction methods
  broadcastFieldFocus(fieldId) {
    if (!fieldId || !this.isConnected) return;

    this.subscription.perform("field_focused", { field_id: fieldId });
  }

  broadcastFieldUpdate(fieldId, value) {
    if (!fieldId || !this.isConnected) return;

    this.subscription.perform("field_updated", {
      field_id: fieldId,
      value: value,
    });
  }

  broadcastFieldBlur(fieldId) {
    if (!fieldId || !this.isConnected) return;

    this.subscription.perform("field_blurred", { field_id: fieldId });
  }

  broadcastFormUpdate(formData) {
    if (!this.isConnected) return;

    this.subscription.perform("form_updated", {
      form_data: formData,
    });
  }

  requestSync() {
    if (!this.isConnected) return;

    this.subscription.perform("request_sync", {});
  }

  broadcastSessionComplete(submissionData = {}) {
    if (!this.isConnected) return;

    this.subscription.perform("session_complete", {
      submission_data: submissionData,
    });
  }

  // Event system
  triggerCallback(eventType, data) {
    if (this.callbacks[eventType]) {
      try {
        this.callbacks[eventType](data);
      } catch (error) {
        console.error(`Error in ${eventType} callback:`, error);
      }
    }
  }
}
